import { useCallback } from 'react';
import {
  TableColumnSettings,
  TableCustomFilterModal,
} from '@asset-metrix/portal-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  deleteFilterCustomization,
  mutateFilterCustomization,
  setFilterCustomizationQueryData,
} from '../../api/portalGateway/tableFilterCustomization';
import { useStore } from '../../store';
import { useDashboardEditModeStore } from '../../store/states/dashboardEditMode';
import { isWidgetTableFilterStoreKey } from '../../utils/filterInstanceId';
import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';

export type TableCustomFilterProps = {
  activeCustomFilterIds: string[];
  customFilterColumns: {
    id: string;
    key: string;
    label: string;
    sublabel?: string;
    disabled?: boolean;
  }[];
  modalOpened: boolean;
  onClose: () => void;
  referenceType: 'content' | 'page';
  reference: string;
  filterInstanceIds: string[];
  withinPortal?: boolean;
};

export function TableCustomFilter({
  activeCustomFilterIds,
  customFilterColumns,
  modalOpened,
  onClose,
  referenceType,
  reference,
  filterInstanceIds,
  withinPortal = true,
}: TableCustomFilterProps) {
  const storeResetFilterSelections = useStore(
    (state) => state.resetFilterSelections
  );

  const resetFilterSelection = useCallback(
    (key?: string) => {
      const ids = new Set(filterInstanceIds);
      if (referenceType === 'content') {
        for (const id of Object.keys(
          useStore.getState().filterSelections ?? {}
        )) {
          if (isWidgetTableFilterStoreKey(id, reference)) {
            ids.add(id);
          }
        }
      }
      for (const id of ids) {
        storeResetFilterSelections(id, key);
      }
    },
    [filterInstanceIds, reference, referenceType, storeResetFilterSelections]
  );

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: mutateFilterCustomization,
    onMutate: ({ referenceType, reference, data }) => {
      setFilterCustomizationQueryData(
        queryClient,
        referenceType,
        reference,
        data
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFilterCustomization,
    onMutate: ({ referenceType, reference }) => {
      setFilterCustomizationQueryData(queryClient, referenceType, reference, {
        columns: [],
      });
    },
  });
  const customFilters: TableColumnSettings[] = [];
  for (const column of customFilterColumns) {
    const { id, label, sublabel } = column;
    customFilters.push({
      id,
      label,
      sublabel,
      isVisible: activeCustomFilterIds.includes(id) || column.disabled === true,
      disabled: column.disabled,
    });
  }

  return (
    <TableCustomFilterModal
      initialValue={customFilters}
      modalOpened={modalOpened}
      onCommit={(newColumnSettings) => {
        if (useDashboardEditModeStore.getState().isSaving) {
          onClose();
          return;
        }
        const visibleFilters = newColumnSettings.filter(
          (colSetting) => colSetting.isVisible && colSetting.disabled !== true
        );
        const columnIds = visibleFilters.map((column) => column.id);
        const keptOnDraft =
          referenceType === 'content' &&
          useDashboardEditModeStore
            .getState()
            .replaceDraftCustomFilterColumnIds(reference, columnIds);
        if (visibleFilters.length > 0) {
          if (!keptOnDraft) {
            updateMutation.mutate({
              referenceType,
              reference,
              data: {
                columns: columnIds.map((columnId) => ({ columnId })),
              },
            });
          }

          // reset selection for all filters that are not active ANYMORE
          const deselectedFilters = newColumnSettings.filter(
            (colSetting) =>
              !colSetting.isVisible &&
              colSetting.disabled !== true &&
              activeCustomFilterIds.includes(colSetting.id)
          );
          for (const deselectedFilter of deselectedFilters) {
            const key = customFilterColumns.find(
              (column) => column.id === deselectedFilter.id
            )?.key;
            if (key != null) {
              resetFilterSelection(key);
            }
          }
        } else {
          if (!keptOnDraft) {
            deleteMutation.mutate({
              referenceType,
              reference,
            });
          }
          resetFilterSelection();
        }
        onClose();
      }}
      onCancel={onClose}
      onReset={() => {
        if (useDashboardEditModeStore.getState().isSaving) {
          onClose();
          return;
        }
        const keptOnDraft =
          referenceType === 'content' &&
          useDashboardEditModeStore
            .getState()
            .replaceDraftCustomFilterColumnIds(reference, []);
        if (!keptOnDraft) {
          deleteMutation.mutate({
            referenceType,
            reference,
          });
        }
        resetFilterSelection();
        onClose();
      }}
      size={pixelSizeHelperPortalUi(650)}
      withinPortal={withinPortal}
    />
  );
}
